import { useSrpContext } from '@/contexts/SrpContext'
import { extractCategoryIdsFromPath } from '@/domain/category/extractCategoryIdsFromPath'
import { useLocale } from '@/hooks/useLocale'
import { useAdSense } from '@/lib/ads/hooks/useAdSense'
import { useFetchAdConfig } from '@/lib/ads/hooks/useFetchAdConfig'
import { useSrpGpt } from '@/lib/ads/hooks/useSrpGpt'
import { generateGotEndpoint } from '@/lib/ads/utils/globalOptimizationTool'

import { getAdSenseConfig } from '../utils/adsense/getAdSenseConfig'
import { cleanCategoryPathForDisplayAds } from '../utils/cleanCategoryPathForDisplayAds'

/**
 * Custom hook to handle the logic for Search Results Page (SRP) advertisements
 * This hook returns the necessary configurations and ad slot positions for the SRP and Gpt config
 *
 * @param limit - The limit for the number of listings per page
 *
 * @returns gpt -
 * @returns gptConfig - The GPT configuration object
 */
export const useSrpAds = (isMobileForAds?: boolean) => {
  const { filters, totalListings, currentPage, categoryPath, keywords, location, category } =
    useSrpContext()

  const { routeLocale } = useLocale()

  const cleanedPath = cleanCategoryPathForDisplayAds(categoryPath)

  const gotEndpoint = generateGotEndpoint(
    totalListings,
    keywords,
    category.id.toString(),
    isMobileForAds
  )

  const allCategoryIds = extractCategoryIdsFromPath(categoryPath)

  const gpt = useSrpGpt({
    categoryIds: allCategoryIds,
    currentPage: currentPage,
    keywords,
    locationId: location?.id,
    routeLocale,
    filters,
    categories: cleanedPath,
    totalListings,
  })

  const { gptConfig } = useFetchAdConfig(gotEndpoint, gpt, routeLocale)

  const adSenseConfig = getAdSenseConfig({
    allCategoryIds: allCategoryIds,
    location,
    currentPage,
    keywords,
    routeLocale,
    categoryPath,
  })

  useAdSense({
    ...adSenseConfig,
    active: true,
    containers: [
      { container: 'AFSTop', width: '100%', number: 2 },
      { container: 'AFSBottom', width: '100%', number: 2 },
    ],
  })

  return {
    gptConfig,
    gpt,
  }
}
